
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { SkillBadge } from "@/components/SkillBadge";
import { ViewMode } from "./types";
import { Briefcase, Building, FileCode } from "lucide-react";
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious 
} from "@/components/ui/pagination";

interface Job {
  id: string;
  title: string;
  company_id: string;
  company_name?: string;
  skills?: string[];
  description?: string;
}

interface JobSearchResultsProps {
  searchQuery: string;
  onResultClick: (id: string) => void;
  viewMode: ViewMode;
}

export function JobSearchResults({ searchQuery, onResultClick, viewMode }: JobSearchResultsProps) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const resultsPerPage = 5;

  useEffect(() => {
    // Reset to first page on new search
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    const searchJobs = async () => {
      if (!searchQuery.trim()) return;

      setIsLoading(true);
      
      try {
        // Get total count for pagination
        const { count, error: countError } = await supabase
          .from("jobs")
          .select("*", { count: 'exact', head: true })
          .or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`);
          
        if (countError) throw countError;
        setTotalJobs(count || 0);
        
        // Search by title or description - case insensitive
        const { data, error } = await supabase
          .from("jobs")
          .select("*, company:company_id(name)")
          .or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`)
          .order("title", { ascending: true })
          .range((currentPage - 1) * resultsPerPage, currentPage * resultsPerPage - 1);

        if (error) throw error;
        
        // Search for jobs with skills case-insensitively
        const lowercaseSearchQuery = searchQuery.toLowerCase();
        const { data: allJobs, error: skillsError } = await supabase
          .from("jobs")
          .select("*, company:company_id(name)")
          .order("title", { ascending: true });
          
        if (skillsError) throw skillsError;
        
        // Manually filter for skills case-insensitively
        const skillsData = allJobs?.filter(job => 
          job.skills?.some(skill => 
            skill.toLowerCase().includes(lowercaseSearchQuery)
          )
        ) || [];
        
        // Combine and deduplicate results
        const combinedResults = [...(data || [])];
        
        skillsData?.forEach(job => {
          if (!combinedResults.some(j => j.id === job.id)) {
            combinedResults.push(job);
          }
        });
        
        // Format the results to include company name and apply pagination
        const formattedJobs = combinedResults
          .map(job => ({
            ...job,
            company_name: job.company?.name
          }))
          .slice(0, resultsPerPage);
        
        setJobs(formattedJobs);
      } catch (error) {
        console.error("Error searching jobs:", error);
      } finally {
        setIsLoading(false);
      }
    };

    searchJobs();
  }, [searchQuery, currentPage]);

  const totalPages = Math.ceil(totalJobs / resultsPerPage);

  if (isLoading) {
    return <div className="flex items-center justify-center h-40 text-gray-500">
      <div className="animate-spin mr-2 h-5 w-5 border-2 border-digger-blue border-t-transparent rounded-full"></div>
      Searching jobs...
    </div>;
  }

  if (jobs.length === 0) {
    return <div className="flex flex-col items-center justify-center h-40 text-gray-500">
      <Briefcase className="h-12 w-12 mb-2 text-gray-400" />
      <p>No jobs found matching "<span className="font-medium">{searchQuery}</span>"</p>
    </div>;
  }

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    return (
      <Pagination className="my-4">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
            />
          </PaginationItem>
          
          {[...Array(Math.min(3, totalPages))].map((_, i) => {
            const pageNumber = i + 1;
            return (
              <PaginationItem key={pageNumber}>
                <PaginationLink
                  onClick={() => setCurrentPage(pageNumber)}
                  isActive={currentPage === pageNumber}
                  className="cursor-pointer"
                >
                  {pageNumber}
                </PaginationLink>
              </PaginationItem>
            );
          })}
          
          {totalPages > 3 && (
            <PaginationItem>
              <PaginationLink
                onClick={() => setCurrentPage(totalPages)}
                isActive={currentPage === totalPages}
                className="cursor-pointer"
              >
                {totalPages}
              </PaginationLink>
            </PaginationItem>
          )}
          
          <PaginationItem>
            <PaginationNext 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {viewMode === "card" ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
            {jobs.map((job) => (
              <div
                key={job.id}
                className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors shadow-sm hover:shadow"
                onClick={() => onResultClick(job.id)}
              >
                <div className="flex items-start mb-2">
                  <div className="h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center mr-3 text-digger-blue">
                    <Briefcase className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-medium text-digger-blue">{job.title}</h3>
                    {job.company_name && (
                      <p className="text-gray-600 text-sm flex items-center mt-1">
                        <Building className="h-3.5 w-3.5 mr-1 inline-block" />
                        {job.company_name}
                      </p>
                    )}
                  </div>
                </div>
                
                {job.skills && job.skills.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {job.skills.slice(0, 5).map((skill) => (
                      <SkillBadge key={skill} label={skill} />
                    ))}
                    {job.skills.length > 5 && (
                      <span className="text-xs text-gray-500 ml-1 self-center">
                        +{job.skills.length - 5} more
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          {renderPagination()}
        </>
      ) : (
        <>
          <div className="overflow-hidden">
            <div className="grid grid-cols-12 bg-muted px-4 py-2.5 text-sm font-medium sticky top-0">
              <div className="col-span-5">Title</div>
              <div className="col-span-3">Company</div>
              <div className="col-span-4">Skills</div>
            </div>
            
            <div>
              {jobs.map((job) => (
                <div 
                  key={job.id} 
                  className="grid grid-cols-12 px-4 py-3 border-b hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => onResultClick(job.id)}
                >
                  <div className="col-span-5 flex items-center gap-2">
                    <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center text-digger-blue">
                      <Briefcase className="h-4 w-4" />
                    </div>
                    <span className="font-medium">{job.title}</span>
                  </div>
                  <div className="col-span-3 flex items-center gap-2">
                    {job.company_name ? (
                      <>
                        <Building className="h-4 w-4 text-gray-400" />
                        {job.company_name}
                      </>
                    ) : (
                      "-"
                    )}
                  </div>
                  <div className="col-span-4 flex flex-wrap gap-1 items-center">
                    {job.skills && job.skills.slice(0, 3).map((skill) => (
                      <SkillBadge key={skill} label={skill} />
                    ))}
                    {job.skills && job.skills.length > 3 && (
                      <span className="text-xs text-gray-500">
                        +{job.skills.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {renderPagination()}
        </>
      )}
    </div>
  );
}
